import React, { useState, useEffect, useRef } from 'react'
import { Component, LocaleCode, BigCommerceProductData } from '../types/component'

interface SearchProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

export function Search({ component, storeId, apiUrl, style }: SearchProps) {
  const config = component.config as any
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<BigCommerceProductData[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const placeholder = config.placeholder || 'Search products...'
  const showAutocomplete = config.showAutocomplete !== false
  const maxResults = config.maxResults || 5
  const showImages = config.showImages !== false
  const showPrice = config.showPrice !== false

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Search products with debounce
  useEffect(() => {
    if (!searchQuery.trim() || !storeId || !showAutocomplete) {
      setResults([])
      setShowDropdown(false)
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true)
        const response = await fetch(
          `${apiUrl}/api/v1/bigcommerce/${storeId}/products/search?keyword=${encodeURIComponent(searchQuery)}&page=1&limit=${maxResults}`
        )
        const data = await response.json()
        setResults(data.products || [])
        setShowDropdown(true)
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, storeId, showAutocomplete, maxResults, apiUrl])

  const handleResultClick = (product: BigCommerceProductData) => {
    setShowDropdown(false)
    setSearchQuery('')

    if (product.url) {
      window.open(product.url, '_blank')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (results.length === 1) {
      handleResultClick(results[0])
    }
  }

  const containerStyle: React.CSSProperties = {
    ...style,
    position: 'relative',
  }

  const formStyle: React.CSSProperties = {
    position: 'relative',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 48px 12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    outline: 'none',
    fontSize: '16px',
  }

  const buttonStyle: React.CSSProperties = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
  }

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 50,
    width: '100%',
    marginTop: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    maxHeight: '384px',
    overflowY: 'auto',
  }

  const resultItemStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'start',
    gap: '12px',
    padding: '12px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.2s',
  }

  return (
    <div style={containerStyle} ref={dropdownRef}>
      <form onSubmit={handleSubmit} style={formStyle}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
          onFocus={(e) => (e.target.style.outline = '2px solid #3b82f6')}
          onBlur={(e) => (e.target.style.outline = 'none')}
        />
        <button type="submit" style={buttonStyle} aria-label="Search">
          {isSearching ? (
            <svg
              style={{ animation: 'spin 1s linear infinite', height: '20px', width: '20px' }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          )}
        </button>
      </form>

      {showAutocomplete && showDropdown && results.length > 0 && (
        <div style={dropdownStyle}>
          {results.map((product) => (
            <button
              key={product.productId}
              onClick={() => handleResultClick(product)}
              style={resultItemStyle}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = '#f3f4f6')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
            >
              {showImages && (
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    flexShrink: 0,
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                        fontSize: '10px',
                      }}
                    >
                      No image
                    </div>
                  )}
                </div>
              )}

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <p
                  style={{
                    fontWeight: '500',
                    fontSize: '14px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.name}
                </p>
                {product.sku && (
                  <p style={{ fontSize: '12px', color: '#9ca3af' }}>
                    SKU: {product.sku}
                  </p>
                )}
              </div>

              {showPrice && (
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  {product.salePrice && product.salePrice < product.price ? (
                    <div>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#ef4444',
                        }}
                      >
                        ${product.salePrice.toFixed(2)}
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          textDecoration: 'line-through',
                        }}
                      >
                        ${product.price.toFixed(2)}
                      </p>
                    </div>
                  ) : (
                    <p style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      ${(product.calculatedPrice || product.price).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {showAutocomplete &&
        showDropdown &&
        searchQuery &&
        !isSearching &&
        results.length === 0 && (
          <div
            style={{
              ...dropdownStyle,
              padding: '16px',
              textAlign: 'center',
              color: '#9ca3af',
            }}
          >
            No products found for "{searchQuery}"
          </div>
        )}
    </div>
  )
}
